import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import adminConsoleService, {
  type SecurityGroup,
  type SecuritySubGroup,
  type PersonListItem,
} from '@services/adminConsoleService';
import { SecuritySubGroupCard } from './SecuritySubGroupCard';
import { AddSubGroupModal } from './AddSubGroupModal';
interface SecurityGroupCardProps {
  group: SecurityGroup;
  onToggle: (groupId: number) => void;
  onRenameGroup: (groupId: number, name: string) => void;
  onDeleteGroup: (groupId: number) => void;
  onAddSubGroup: (groupId: number) => void;
  onRenameSubGroup: (subGroupId: number, name: string) => void;
  onDeleteSubGroup: (subGroupId: number) => void;
  onUpdateSubGroup: (subGroupId: number, updates: Partial<SecuritySubGroup>) => void;
  onUpdateGroup: (groupId: number, updates: Partial<SecurityGroup>) => void;
}

export const SecurityGroupCard: React.FC<SecurityGroupCardProps> = ({
  group,
  onToggle,
  onRenameGroup,
  onDeleteGroup,
  onAddSubGroup,
  onRenameSubGroup,
  onDeleteSubGroup,
  onUpdateSubGroup,
  onUpdateGroup,
}) =>
{
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(group.Name);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const [adminUsersOpen, setAdminUsersOpen] = useState(false);
  const [peopleList, setPeopleList] = useState<PersonListItem[]>([]);
  const [loadingPeople, setLoadingPeople] = useState(false);
  const [selectedAdminIds, setSelectedAdminIds] = useState<Set<number>>(new Set());
  const [adminSearch, setAdminSearch] = useState('');
  const [savingAdmins, setSavingAdmins] = useState(false);
  const [adminError, setAdminError] = useState<string | null>(null);
  const [adminInputFocused, setAdminInputFocused] = useState(false);
  const [showAddSubGroup, setShowAddSubGroup] = useState(false);
  const menuBtnRef = useRef<HTMLButtonElement>(null);

  // Collect unique layers (by LayerId) from all sub-groups, sorted by LayerId
  const layerMap = new Map<number, string>();
  for (const sg of group.SecurityGroups)
  {
    if (sg.IsDeleted) continue;
    for (const l of sg.SecurityGroupLayers)
    {
      if (!layerMap.has(l.LayerId)) layerMap.set(l.LayerId, l.Color);
    }
  }
  const layerColors = [...layerMap.entries()].sort((a, b) => a[0] - b[0]).map(([, color]) => color);

  // Close menu on outside click
  useEffect(() =>
  {
    if (!menuOpen) return;
    const close = (e: MouseEvent) =>
    {
      const menu = document.querySelector('[data-sg-menu]');
      if (menu && menu.contains(e.target as Node)) return;
      if (menuBtnRef.current && menuBtnRef.current.contains(e.target as Node)) return;
      setMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  const openMenu = () =>
  {
    if (!menuBtnRef.current) return;
    const rect = menuBtnRef.current.getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 4, left: rect.right - 150 });
    setMenuOpen(true);
  };

  const commitRename = () =>
  {
    const trimmed = nameValue.trim();
    if (trimmed && trimmed !== group.Name) onRenameGroup(group.GroupId, trimmed);
    else setNameValue(group.Name);
    setEditingName(false);
  };

  const openAdminUsers = async () =>
  {
    setAdminUsersOpen(true);
    setAdminSearch('');
    setAdminError(null);
    setLoadingPeople(true);
    try
    {
      const people = await adminConsoleService.getPeopleList();
      setPeopleList(people);
      setSelectedAdminIds(
        new Set(
          group.GroupAdminUsers.map(u => u.PersonID ?? u.PersonId).filter(Boolean) as number[],
        ),
      );
    }
    catch
    {
      setAdminError('Failed to load users.');
    }
    finally
    {
      setLoadingPeople(false);
    }
  };

  const saveAdminUsers = async () =>
  {
    setSavingAdmins(true);
    setAdminError(null);
    try
    {
      const ids = [...selectedAdminIds];
      await adminConsoleService.saveUsersGroup(group.GroupId, ids);
      const updatedAdmins = peopleList
        .filter(p => selectedAdminIds.has(p.PersonID))
        .map(p => ({
          PersonID: p.PersonID,
          PersonId: p.PersonID,
          Name: p.Name,
          PathImage: p.PathImage,
        }));
      onUpdateGroup(group.GroupId, { GroupAdminUsers: updatedAdmins });
      setAdminUsersOpen(false);
    }
    catch
    {
      setAdminError('Failed to save admin users.');
    }
    finally
    {
      setSavingAdmins(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) =>
  {
    if (e.key === 'Enter') commitRename();
    if (e.key === 'Escape')
    {
      setNameValue(group.Name);
      setEditingName(false);
    }
  };

  const activeSubGroups = group.SecurityGroups.filter(sg => !sg.IsDeleted);

  return (
    <div className="border-b border-[#e8e8e8] bg-white">
      {/* ── Layer color bar (top, right-aligned, sorted by LayerId) ── */}
      {layerColors.length > 0 && (
        <div className="flex justify-end h-[4px]">
          {layerColors.map((color, i) => (
            <div key={i} style={{ width: 64, flexShrink: 0, backgroundColor: color }} />
          ))}
        </div>
      )}

      {/* ── Main row ── */}
      <div className="flex items-center gap-3 px-3 py-2.5 hover:bg-[#fafafa] transition-colors">
        {/* Expand arrow */}
        <button
          onClick={() => onToggle(group.GroupId)}
          className="text-[#aaa] hover:text-[#555] transition-colors flex-shrink-0 w-5"
        >
          <span className="material-icons text-[16px]">
            {group.IsOpen ? 'expand_more' : 'chevron_right'}
          </span>
        </button>

        {/* Grid icon + name */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <i className="material-icons text-[18px] text-[#666] dark:!text-[#b9b1a6]">
            location_city
          </i>
          {editingName ? (
            <input
              autoFocus
              type="text"
              value={nameValue}
              onChange={e => setNameValue(e.target.value)}
              onBlur={commitRename}
              onKeyDown={handleKeyDown}
              className="border border-[#62aacb] rounded px-1 py-0.5 text-[13px] font-medium text-[#333] outline-none max-w-[260px] dark:!text-[#b9b1a6]"
            />
          ) : (
            <span
              className="text-[13px] font-medium text-[#333] cursor-default truncate dark:!text-[#b9b1a6]"
              onDoubleClick={() => setEditingName(true)}
              title={group.Name}
            >
              {group.Name}
            </span>
          )}
        </div>

        {/* Admin Users */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-[11px] text-[#aaa] dark:!text-[#b9b1a6]">Admin Users:</span>
          <div className="flex items-center gap-0.5">
            {group && group.GroupAdminUsers && group.GroupAdminUsers.length === 0 ? (
              <span className="text-[11px] text-[#ccc]">—</span>
            ) : (
              <>
                {group &&
                  group.GroupAdminUsers &&
                  group.GroupAdminUsers.slice(0, 5).map((u, i) =>
                    u.PathImage ? (
                      <div
                        key={i}
                        title={u.Name}
                        className="w-[22px] h-[22px] rounded-sm bg-[#ddd] bg-cover bg-center border border-[#e0e0e0]"
                        style={{ backgroundImage: `url(${u.PathImage})` }}
                      />
                    ) : (
                      <div
                        key={i}
                        title={u.Name}
                        className="w-[22px] h-[22px] rounded-sm bg-[#ddd] flex items-center justify-center border border-[#e0e0e0]"
                      >
                        <span className="material-icons text-[14px] text-[#999]">person</span>
                      </div>
                    ),
                  )}
                {group && group.GroupAdminUsers && group.GroupAdminUsers.length > 5 && (
                  <span className="text-[10px] text-[#999] ml-0.5">
                    +{group.GroupAdminUsers.length - 5}
                  </span>
                )}
              </>
            )}
          </div>
        </div>

        {/* 3-dot kebab menu */}
        <button
          ref={menuBtnRef}
          onClick={openMenu}
          className="text-[#bbb] hover:text-[#666] transition-colors flex-shrink-0 px-1"
          title="Options"
        >
          <span className="material-icons text-[18px]">more_vert</span>
        </button>
      </div>

      {/* ── Expanded sub-groups ── */}
      {group.IsOpen && (
        <div className="px-4 py-3 bg-[#fafafa] border-t border-[#f0f0f0] dark:bg-[#3b3d3d]">
          {activeSubGroups.length === 0 ? (
            <div className="text-[12px] text-[#bbb] text-center py-3 italic">
              No security groups yet.{' '}
              <button
                onClick={() => setShowAddSubGroup(true)}
                className="text-[#62aacb] hover:underline not-italic"
              >
                Add one
              </button>
            </div>
          ) : (
            <>
              {activeSubGroups.map(sg => (
                <SecuritySubGroupCard
                  key={sg.SecurityGroupID}
                  subGroup={sg}
                  onRename={onRenameSubGroup}
                  onDelete={onDeleteSubGroup}
                  onUpdate={onUpdateSubGroup}
                />
              ))}
              <button
                onClick={() => setShowAddSubGroup(true)}
                className="text-[12px] text-[#62aacb] hover:underline flex items-center gap-1 mx-auto mt-3 group"
              >
                <span className="material-icons text-[14px]">add_circle_outline</span>
                <span className="group-hover:underline">Add New Security Sub-Group</span>
              </button>
            </>
          )}
        </div>
      )}

      {/* ── Kebab dropdown (portal) ── */}
      {menuOpen &&
        createPortal(
          <div
            data-sg-menu
            className="admin-console-portal fixed z-[10002] bg-white border border-[#ddd] rounded shadow-lg py-1 w-[180px] text-[12px] dark:bg-[#3b3d3d] dark:border-[transparent]"
            style={{ top: menuPos.top, left: menuPos.left }}
          >
            <button
              className="w-full text-left px-3 py-2 hover:bg-[#f5f5f5] dark:hover:bg-[#4a4c4c] text-[#333] flex items-center gap-2"
              onClick={() =>
              {
                setMenuOpen(false);
                openAdminUsers();
              }}
            >
              <i className="icon-pencil-icon text-[#888]" />
              Manage Admin Users
            </button>
            {!group.IsDefault && (
              <>
                <div className="border-t border-[#eee] my-1" />
                <button
                  className="w-full text-left px-3 py-2 hover:bg-[#fdecea] text-[#e57373] dark:hover:bg-[#4a4c4c] flex items-center gap-2"
                  onClick={() =>
                  {
                    setMenuOpen(false);
                    onDeleteGroup(group.GroupId);
                  }}
                >
                  <i className="icon-trash-icon " />
                  Delete Security Group
                </button>
              </>
            )}
          </div>,
          document.body,
        )}

      {/* ── Admin Users Modal ── */}
      {adminUsersOpen &&
        createPortal(
          <div className="admin-console-portal fixed inset-0 z-[10003] flex items-center justify-center">
            <div className="fixed inset-0 bg-black/30" onClick={() => setAdminUsersOpen(false)} />
            <div className="relative z-10 bg-white rounded shadow-xl w-[460px] max-h-[80vh] flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between px-5 pt-4 pb-3">
                <h3 className="text-[16px] font-medium text-[#62aacb]">Admin Users</h3>
                <button
                  onClick={() => setAdminUsersOpen(false)}
                  className="text-[#bbb] hover:text-[#666]"
                >
                  <span className="material-icons text-[18px]">close</span>
                </button>
              </div>

              {/* Dropdown para agregar */}
              <div className="px-5 pb-3">
                <span className="text-[12px] text-[#555] block mb-1">Select user:</span>
                <div className="relative">
                  <input
                    autoFocus
                    type="text"
                    value={adminSearch}
                    onChange={e => setAdminSearch(e.target.value)}
                    onFocus={() => setAdminInputFocused(true)}
                    onBlur={() => setTimeout(() => setAdminInputFocused(false), 150)}
                    placeholder="Search and add user..."
                    className="w-full border border-[#ccc] rounded px-2 py-1.5 text-[12px] outline-none focus:border-[#62aacb]"
                  />
                  {/* Dropdown results */}
                  {adminInputFocused && (
                    <div className="absolute top-full left-0 right-0 z-10 bg-white border border-[#ddd] rounded shadow-lg max-h-[180px] overflow-y-auto mt-0.5">
                      {loadingPeople ? (
                        <div className="flex items-center justify-center py-4">
                          <span className="material-icons animate-spin text-[#62aacb] text-[20px]">
                            refresh
                          </span>
                        </div>
                      ) : peopleList.filter(
                        p =>
                          p.Name.toLowerCase().includes(adminSearch.toLowerCase()) &&
                            !selectedAdminIds.has(p.PersonID),
                      ).length === 0 ? (
                          <div className="text-[12px] text-[#aaa] text-center py-3 italic">
                          No results.
                          </div>
                        ) : (
                          peopleList
                            .filter(
                              p =>
                                p.Name.toLowerCase().includes(adminSearch.toLowerCase()) &&
                              !selectedAdminIds.has(p.PersonID),
                            )
                            .map(p => (
                              <button
                                key={p.PersonID}
                                onClick={() =>
                                {
                                  setSelectedAdminIds(prev => new Set(prev).add(p.PersonID));
                                  setAdminSearch('');
                                }}
                                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[#f0f8ff] text-left border-b border-[#f5f5f5]"
                              >
                                {p.PathImage ? (
                                  <div
                                    className="w-[22px] h-[22px] rounded-sm bg-cover bg-center border border-[#e0e0e0] flex-shrink-0"
                                    style={{ backgroundImage: `url(${p.PathImage})` }}
                                  />
                                ) : (
                                  <div className="w-[22px] h-[22px] rounded-sm bg-[#ddd] flex items-center justify-center border border-[#e0e0e0] flex-shrink-0">
                                    <span className="material-icons text-[12px] text-[#999]">
                                    person
                                    </span>
                                  </div>
                                )}
                                <span className="text-[12px] text-[#333]">{p.Name}</span>
                              </button>
                            ))
                        )}
                    </div>
                  )}
                </div>
              </div>

              {/* Lista de admins seleccionados */}
              <div className="flex-1 overflow-hidden mx-5 mb-3 border border-[#ddd] rounded flex flex-col">
                <div className="px-3 py-1.5 bg-[#f5f5f5] border-b border-[#ddd] text-[11px] font-semibold text-[#555]">
                  Name
                </div>
                <div className="flex-1 overflow-y-auto">
                  {selectedAdminIds.size === 0 ? (
                    <div className="text-[12px] text-[#aaa] text-center py-6 italic">
                      No admin users added.
                    </div>
                  ) : (
                    peopleList
                      .filter(p => selectedAdminIds.has(p.PersonID))
                      .map(p => (
                        <div
                          key={p.PersonID}
                          className="flex items-center gap-3 px-3 py-2 border-b border-[#f0f0f0] hover:bg-[#f9f9f9]"
                        >
                          {p.PathImage ? (
                            <div
                              className="w-[26px] h-[26px] rounded-sm bg-cover bg-center border border-[#e0e0e0] flex-shrink-0"
                              style={{ backgroundImage: `url(${p.PathImage})` }}
                            />
                          ) : (
                            <div className="w-[26px] h-[26px] rounded-sm bg-[#ddd] flex items-center justify-center border border-[#e0e0e0] flex-shrink-0">
                              <span className="material-icons text-[14px] text-[#999]">person</span>
                            </div>
                          )}
                          <span className="text-[13px] text-[#333] flex-1">{p.Name}</span>
                          <button
                            onClick={() =>
                              setSelectedAdminIds(prev =>
                              {
                                const next = new Set(prev);
                                next.delete(p.PersonID);
                                return next;
                              })
                            }
                            className="text-[#bbb] hover:text-[#e57373] transition-colors"
                          >
                            <span className="material-icons text-[16px]">close</span>
                          </button>
                        </div>
                      ))
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="px-5 py-3 border-t border-[#eee] flex justify-end gap-2">
                {adminError && (
                  <span className="text-[11px] text-[#e57373] flex-1">{adminError}</span>
                )}
                <button
                  onClick={() => setAdminUsersOpen(false)}
                  className="px-4 py-1.5 text-[12px] bg-[#999] text-white rounded hover:bg-[#777] dark:bg-[#5c6060] dark:hover:brightness-[0.85]"
                >
                  Cancel
                </button>
                <button
                  onClick={saveAdminUsers}
                  disabled={savingAdmins}
                  className="px-4 py-1.5 text-[12px] bg-[#62aacb] text-white rounded hover:bg-[#4e99ba] dark:bg-[#3c6c80] dark:hover:brightness-[0.85] disabled:opacity-50"
                >
                  {savingAdmins ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {showAddSubGroup && (
        <AddSubGroupModal
          groupId={group.GroupId}
          onClose={() => setShowAddSubGroup(false)}
          onSave={() =>
          {
            onAddSubGroup(group.GroupId);
            setShowAddSubGroup(false);
          }}
        />
      )}
    </div>
  );
};
